import { fromEvent, map } from "rxjs";
import { store } from '@redux/store';
import { userProfileActions } from 'features/userProfile';

export default class RealtorDropdown {
  constructor() {
    this.selectedValue = '';
    this.required = false;
    this.errorMessageId = 'nhs_ErrorForRealtorQuestion';
    this.errorCssClass = 'error'
    this.id = 'nhs-real-agent';
    this.labelClassName = '';
    this.fieldClassName = 'form__select';
    this.errorClassName = 'form__error';
  }

  set value(newValue) {
    this.selectedValue = newValue || '';
    const realtorDropdown = this.getNode();
    if (realtorDropdown) {
      realtorDropdown.value = this.selectedValue;
    }
  }

  get value() {
    return this.selectedValue;
  }

  /**
   * Overrides the CSS classes used by getHtml(). Defaults (set in the constructor) match every
   * existing consumer of this component; only screens that want different styling need to call
   * this — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {Object} classNames
   * @param {string} [classNames.labelClassName]
   * @param {string} [classNames.fieldClassName]
   * @param {string} [classNames.errorClassName]
   */
  setClassNames({ labelClassName, fieldClassName, errorClassName } = {}) {
    if (labelClassName !== undefined) { this.labelClassName = labelClassName; }
    if (fieldClassName !== undefined) { this.fieldClassName = fieldClassName; }
    if (errorClassName !== undefined) { this.errorClassName = errorClassName; }
    return this;
  }

  /**
   * Overrides the element id used by getHtml()/getNode(). Default (set in the constructor) matches
   * every existing consumer of this component; a screen that can render alongside another instance
   * of this component on the same page (e.g. a persistent sidebar form) must override this to avoid
   * a duplicate-id collision — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} id
   */
  setInputId(id) {
    this.id = id;
    return this;
  }

  getHtml() {
    return `<label for="${this.id}" class="${this.labelClassName}" data-qa="realtorText">Are you working with a real estate agent?</label>
      <div class="nhs-u-width--full">
        <select id="${this.id}" class="${this.fieldClassName}" autocomplete="off" name="realtorQuestion" data-qa="realtorQuestion" data-realtor-answer
        ${this.required ? 'required' : ''}>
          <option value="" style="display: none;">Please select an answer</option>
          <option class="nhs-real-agent-option" value="1" data-qa="realtorQuestionOne">I am buying without an agent</option>
          <option class="nhs-real-agent-option" value="2" data-qa="realtorQuestionTwo">I am working with an agent</option>
          <option class="nhs-real-agent-option" value="3" data-qa="realtorQuestionThree">I am a real estate agent</option>
          <option class="nhs-real-agent-option" value="4" data-qa="realtorQuestionFour">I don't know yet</option>
        </select>
        <span id="${this.errorMessageId}" class="${this.errorClassName}" hidden>
                           Please make a selection
                         </span>
      </div>`;
  }

  getNode() {
    return document.getElementById(this.id);
  }

  /**
   * setSelectedValue: Finds the correct index based on the selectedValue
   * and updates the select element.
   */
  setSelectedValue() {
    const realtorDropdownNode = this.getNode();
    if (realtorDropdownNode) {
      const optionsArray = Array.from(realtorDropdownNode.options);
      realtorDropdownNode.selectedIndex = optionsArray.findIndex(
        (option) => option.value === this.selectedValue
      );
    }
  }

  /**
   * getOnChangeEvent: On select change map its value to the storage property.
   */
  getOnChangeEvent() {
    return fromEvent(this.getNode(), 'change').pipe(
      map((event) => {
        const value = event.target.value;
        this.value = value;
        // Omit realtorAnswerId when empty so merging preserves previously stored values.
        if (value) {
          store.dispatch(userProfileActions.update({ realtorAnswerId: value }));
        }
        return { realtorQuestion: this.selectedValue };
      })
    );
  }

  /**
   * Method to remove the required attribute.
   */

  removeRequired() {
    const node = this.getNode();
    node.removeAttribute('required');
  }

  setInvalidState(node) {
    const errorMessage = document.getElementById(this.errorMessageId);

    node.setAttribute('aria-invalid', true);
    node.classList.add(this.errorCssClass);
    errorMessage.removeAttribute('hidden');
  }

  /**
   * Method to set the required attribute.
   */

  setRequired() {
    const node = this.getNode();
    node.setAttribute('required', 'true');
  }

  setValidState(node) {
    const errorMessage = document.getElementById(this.errorMessageId);

    node.removeAttribute('aria-invalid');
    node.classList.remove(this.errorCssClass);
    errorMessage.setAttribute('hidden', true);
  }

  isValid() {
    const node = this.getNode();
    const isEmpty = node.value === '';
    const isRequired = node.required;

    // If it is empty and is required, the input is not valid
    if(isEmpty && isRequired) {
      return false;
    }

    return true;
  }

  validate() {
    const node = this.getNode();
    const isValid = this.isValid();

    return new Promise((resolve) => {
      if (isValid) {
        this.setValidState(node);
        resolve(true);
      } else {
        this.setInvalidState(node);
        resolve(false);
      }
    });
  }
}
